import type {
  ProjectColumnDraft,
  ProjectCreateInput,
  ProjectMember,
  ProjectMemberDraft,
  ProjectMemberRole,
  ProjectMembersAddInput,
} from '../types/project';
import { PROJECT_MEMBER_ROLES } from '../types/project';

let draftKey = 0;

export function nextProjectDraftKey(prefix: string): string {
  draftKey += 1;
  return `${prefix}-${draftKey}`;
}

export const UNLIMITED_TASK_LIMIT = 9999;

export function isUnlimitedTaskLimit(
  value: number | null | undefined,
): boolean {
  return value == null || !Number.isFinite(value) || value >= UNLIMITED_TASK_LIMIT;
}

export function formatColumnTaskCount(
  taskCount: number,
  taskLimit: number | null | undefined,
): string {
  if (isUnlimitedTaskLimit(taskLimit)) {
    return `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`;
  }
  return `${taskCount} / ${taskLimit} tasks`;
}

export function normalizeStoredTaskLimit(
  value: number | null | undefined,
): number | null {
  if (value == null || !Number.isFinite(value) || value >= UNLIMITED_TASK_LIMIT) {
    return null;
  }
  return value;
}

export function createDefaultProjectColumns(): ProjectColumnDraft[] {
  return [
    {
      key: nextProjectDraftKey('column'),
      columnName: 'To-do',
      taskLimit: '',
      isCompletedColumn: false,
      isDefaultColumn: true,
      fixedSlot: 'start',
    },
    {
      key: nextProjectDraftKey('column'),
      columnName: 'Do Today',
      taskLimit: '',
      isCompletedColumn: false,
      isDefaultColumn: true,
    },
    {
      key: nextProjectDraftKey('column'),
      columnName: 'In Progress',
      taskLimit: '',
      isCompletedColumn: false,
      isDefaultColumn: true,
    },
    {
      key: nextProjectDraftKey('column'),
      columnName: 'Done',
      taskLimit: '',
      isCompletedColumn: true,
      isDefaultColumn: true,
      fixedSlot: 'end',
    },
  ];
}

export function createEmptyProjectColumn(): ProjectColumnDraft {
  return {
    key: nextProjectDraftKey('column'),
    columnName: '',
    taskLimit: '',
    isCompletedColumn: false,
    isDefaultColumn: false,
    fixedSlot: null,
  };
}

export function isFixedProjectColumn(
  column: Pick<ProjectColumnDraft, 'fixedSlot'>,
): boolean {
  return column.fixedSlot === 'start' || column.fixedSlot === 'end';
}

export function pinFixedProjectColumns<T extends Pick<ProjectColumnDraft, 'fixedSlot'>>(
  columns: T[],
): T[] {
  const start = columns.filter(column => column.fixedSlot === 'start');
  const end = columns.filter(column => column.fixedSlot === 'end');
  const middle = columns.filter(
    column => column.fixedSlot !== 'start' && column.fixedSlot !== 'end',
  );
  return [...start, ...middle, ...end];
}

export function isProjectMemberRole(value: string): value is ProjectMemberRole {
  return PROJECT_MEMBER_ROLES.some(role => role.value === value);
}

export function projectMemberRoleLabel(role: ProjectMemberRole): string {
  return PROJECT_MEMBER_ROLES.find(item => item.value === role)?.label ?? role;
}

function trimmed(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/**
 * A person's name for project member lists. An employee number stored in the
 * name column is not a name. Name parts from the employee record are used
 * when the primary field is empty or only the number.
 */
export function projectPersonName(input: {
  employeeName?: string | null;
  displayName?: string | null;
  employeeNumber?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): string {
  const number = trimmed(input.employeeNumber);
  const person = (value: string | null | undefined): string | null => {
    const name = trimmed(value);
    if (!name) return null;
    if (number && name.toLowerCase() === number.toLowerCase()) return null;
    return name;
  };
  const joined = [input.firstName, input.middleName, input.lastName]
    .map(trimmed)
    .filter(Boolean)
    .join(' ');
  return (
    person(input.employeeName) ??
    person(input.displayName) ??
    person(joined) ??
    (number || 'Employee')
  );
}

export function validateProjectForm(input: {
  projectName: string;
  members: ProjectMemberDraft[];
  pendingEmployeeId: number | null;
  pendingRole: ProjectMemberRole | null;
  columns: ProjectColumnDraft[];
}): string | null {
  if (!input.projectName.trim()) return 'Project name is required.';

  if (input.pendingEmployeeId != null && !input.pendingRole) {
    return 'Select a role for the chosen employee.';
  }

  const employeeIds = new Set<number>();
  for (const member of input.members) {
    if (!member.employeeId) return 'Each member must have an employee.';
    if (!member.role) return 'Each member must have a role.';
    if (employeeIds.has(member.employeeId)) {
      return 'The same employee cannot be assigned twice.';
    }
    employeeIds.add(member.employeeId);
  }

  if (input.columns.length < 2) {
    return 'To-do and Done columns are required.';
  }

  const columns = pinFixedProjectColumns(input.columns);
  const start = columns[0];
  const end = columns[columns.length - 1];
  if (start?.fixedSlot !== 'start' || start.columnName.trim() !== 'To-do') {
    return 'The To-do column cannot be renamed or moved.';
  }
  if (
    end?.fixedSlot !== 'end' ||
    end.columnName.trim() !== 'Done' ||
    !end.isCompletedColumn
  ) {
    return 'The Done column cannot be renamed or moved.';
  }

  const names = new Set<string>();
  let completedCount = 0;
  for (const column of columns) {
    const name = column.columnName.trim();
    if (!name) return 'Column name is required.';
    const key = name.toLowerCase();
    if (names.has(key)) {
      return 'Column names must be unique within the project.';
    }
    names.add(key);
    if (column.taskLimit.trim() !== '') {
      const taskLimit = Number(column.taskLimit);
      if (!Number.isInteger(taskLimit) || taskLimit <= 0) {
        return 'Task limit must be a number greater than 0.';
      }
      if (taskLimit > 9999) return 'Task limit must not exceed 9999.';
    }
    if (column.isCompletedColumn) completedCount += 1;
  }

  if (completedCount !== 1) {
    return 'Select exactly one completed-task column.';
  }

  return null;
}

/**
 * Edit Project: why `employeeId` cannot be added, or null when it can. An
 * employee already on the project or already queued is rejected.
 */
export function projectMemberConflict(
  employeeId: number,
  existing: ReadonlyArray<Pick<ProjectMember, 'employeeId'>>,
  pending: ReadonlyArray<Pick<ProjectMemberDraft, 'employeeId'>>,
): string | null {
  if (existing.some(member => member.employeeId === employeeId)) {
    return 'This employee is already a member of the project.';
  }
  if (pending.some(member => member.employeeId === employeeId)) {
    return 'The same employee cannot be assigned twice.';
  }
  return null;
}

export function toProjectMembersAddPayload(
  members: ProjectMemberDraft[],
): ProjectMembersAddInput {
  return {
    members: members.map(member => ({
      employeeId: member.employeeId,
      role: member.role,
    })),
  };
}

export function toProjectCreatePayload(input: {
  projectName: string;
  description?: string;
  members: ProjectMemberDraft[];
  columns: ProjectColumnDraft[];
}): ProjectCreateInput {
  return {
    projectName: input.projectName.trim(),
    description: input.description?.trim() || null,
    members: input.members.map(member => ({
      employeeId: member.employeeId,
      role: member.role,
    })),
    columns: pinFixedProjectColumns(input.columns).map((column, index) => ({
      columnName: column.columnName.trim(),
      taskLimit: column.taskLimit.trim()
        ? Number(column.taskLimit)
        : UNLIMITED_TASK_LIMIT,
      isCompletedColumn: column.isCompletedColumn,
      isDefaultColumn: column.isDefaultColumn,
      displayOrder: index + 1,
    })),
  };
}
